import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Upload } from "lucide-react";
import type { WorkerImportResult } from "@crewledger/shared/schemas";
import { EmptyState, PageHeader } from "@/components/design";
import { Button } from "@/components/ui/button";
import { downloadWorkerTemplate, importWorkersCsv } from "./api";
import { PrivacyHelper } from "./components/worker-status-badge";
import { useWorkerPermissions } from "./use-worker-permissions";

export function WorkerImportPage() {
  const navigate = useNavigate();
  const { canWrite } = useWorkerPermissions();
  const [csv, setCsv] = useState("");
  const [result, setResult] = useState<WorkerImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!canWrite) {
    return (
      <PageHeader title="Access denied" description="You do not have permission to import workers." />
    );
  }

  const handleImport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await importWorkersCsv(csv);
      setResult(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (file: File) => {
    void file.text().then(setCsv);
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadWorkerTemplate();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not download template");
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Import workers"
        description="Bulk upload crew roster from CSV. Duplicate employee IDs are skipped."
      />

      <PrivacyHelper />

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" type="button" onClick={() => void handleDownloadTemplate()}>
          <Download className="mr-1.5 h-4 w-4" />
          Download template
        </Button>
        <label className="inline-flex cursor-pointer items-center rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-muted">
          <Upload className="mr-1.5 h-4 w-4" />
          Upload file
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
            }}
          />
        </label>
      </div>

      <textarea
        value={csv}
        onChange={(e) => { setCsv(e.target.value); }}
        rows={12}
        placeholder="Paste CSV content or upload a file…"
        className="w-full rounded-xl border border-input bg-card-white p-4 font-mono text-xs"
      />

      {error ? <p className="text-sm text-red-danger">{error}</p> : null}

      {result ? (
        <div className="rounded-xl border bg-card-white p-4">
          <p className="font-semibold text-navy-deep">
            Imported {result.created} worker{result.created === 1 ? "" : "s"}
            {result.skipped > 0 ? ` · ${result.skipped} skipped` : ""}
          </p>
          {result.errors.length > 0 ? (
            <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto text-sm text-muted-foreground">
              {result.errors.map((e) => (
                <li key={`${e.row}-${e.employeeId}`}>
                  Row {e.row} ({e.employeeId}): {e.message}
                </li>
              ))}
            </ul>
          ) : null}
          <Button className="mt-4" onClick={() => navigate("/workers")}>
            View workers
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button onClick={() => void handleImport()} disabled={!csv.trim() || loading}>
            {loading ? "Importing…" : "Run import"}
          </Button>
          <Link to="/workers">
            <Button variant="outline">Cancel</Button>
          </Link>
        </div>
      )}

      {!csv && !result ? (
        <EmptyState
          icon={Upload}
          title="No CSV loaded"
          description="Download the template, fill in your crew roster, then paste or upload here."
        />
      ) : null}
    </div>
  );
}
